import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { MarkEmailUnread } from "@material-symbols-svg/react/rounded/icons/mark-email-unread";
import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { Field } from "@/shared/components/ui/field";
import { Input } from "@/shared/components/ui/input";
import { useDocumentTitle } from "@/shared/i18n";
import { AuthCard } from "./auth-card";
import {
  AUTH_INPUT_STYLES,
  AuthBackButton,
  AuthFieldError,
  AuthFieldLabel,
  AuthHeading,
  AuthSubmitButton,
} from "./auth-fields";
import { AuthStatusPanel } from "./auth-status-panel";
import { useLastEmailStore } from "./last-email-store";
import { createResetPasswordSchema, type ResetPasswordInput } from "./schema";

const FORM_ID = "reset-password";
const EMAIL_ID = `${FORM_ID}-email`;
const EMAIL_ERROR_ID = `${EMAIL_ID}-error`;

type ResetPasswordScreenProps = {
  /** Sends the reset email; rejects (with a display-ready message) only for
   *  client/network errors — enumeration-safe cases resolve. */
  onRequestReset: (email: string) => Promise<void>;
  /** Return to the sign-in view. */
  onBack: () => void;
};

/** Reset-password request: email form, swaps to the email-sent panel. */
export function ResetPasswordScreen({
  onRequestReset,
  onBack,
}: ResetPasswordScreenProps) {
  const { t } = useTranslation("auth");
  useDocumentTitle(t("pageTitles.resetPassword"));
  // Prefill with the email carried over from sign-in.
  const lastEmail = useLastEmailStore((s) => s.email);
  const setLastEmail = useLastEmailStore((s) => s.setEmail);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const schema = useMemo(() => createResetPasswordSchema(t), [t]);
  const form = useForm<ResetPasswordInput>({
    resolver: standardSchemaResolver(schema),
    defaultValues: { email: lastEmail },
    mode: "onSubmit",
  });

  const isFilled = form.watch("email").trim().length > 0;
  const isSubmitting = form.formState.isSubmitting;

  const onSubmit = form.handleSubmit(async ({ email }) => {
    try {
      await onRequestReset(email);
      setLastEmail(email);
      setSentTo(email);
    } catch (error) {
      form.setError("email", {
        type: "server",
        // A rejection without a message, or an Error with an empty one, is
        // still a failed request, not a problem with the address the learner
        // typed.
        message:
          error instanceof Error && error.message
            ? error.message
            : t("errors.unexpected"),
      });
    }
  });

  if (sentTo) {
    return (
      <AuthStatusPanel
        icon={MarkEmailUnread}
        title={t("resetPassword.sent.title")}
        description={
          <Trans
            t={t}
            i18nKey="resetPassword.sent.description"
            values={{ email: sentTo }}
          />
        }
      />
    );
  }

  return (
    <AuthCard>
      <AuthBackButton onClick={onBack} />

      <AuthHeading
        title={t("resetPassword.title")}
        description={t("resetPassword.description")}
      />

      <form
        id={FORM_ID}
        onSubmit={onSubmit}
        noValidate
        className="flex flex-col gap-6"
      >
        <Controller
          name="email"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <AuthFieldLabel htmlFor={EMAIL_ID}>
                {t("signIn.emailLabel")}
              </AuthFieldLabel>
              <Input
                {...field}
                id={EMAIL_ID}
                type="email"
                autoComplete="email"
                placeholder={t("signIn.emailPlaceholder")}
                aria-invalid={fieldState.invalid}
                aria-describedby={
                  fieldState.invalid ? EMAIL_ERROR_ID : undefined
                }
                className={AUTH_INPUT_STYLES}
              />
              {fieldState.error?.message && (
                <AuthFieldError id={EMAIL_ERROR_ID}>
                  {fieldState.error.message}
                </AuthFieldError>
              )}
            </Field>
          )}
        />

        <AuthSubmitButton active={isFilled} submitting={isSubmitting}>
          {t("resetPassword.submit")}
        </AuthSubmitButton>
      </form>
    </AuthCard>
  );
}
